import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import $ from 'jquery';
import path from 'path';
import {
  Typography,
  Grid,
  Switch,
  makeStyles,
  CircularProgress,
  IconButton,
} from '@material-ui/core';
import { useLanguage, useAuth, useAlert, useAiStagingOrder } from 'hooks';
import { hideModal, fetchAiStagingOrder } from 'state/actions';
import { MODAL, COLOR, ALERT } from 'config/constants';
import { HvIconButton, HvModal, HvButton, HvBeforeAfterSlider } from 'components';
import { downloadSingleFile } from 'utils';
import { images } from 'assets/images';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { createAiStagingIteration } from 'apis';
import classNames from 'classnames';
import RemoveCircleOutlineRoundedIcon from '@material-ui/icons/RemoveCircleOutlineRounded';
import AddCircleOutlineRoundedIcon from '@material-ui/icons/AddCircleOutlineRounded';
import { VARIATION_LIMIT } from './aiServiceSteps/stepsConfig';
import {
  roomStyles,
  getTitle,
  getRoomType,
  getRoomStyleImage,
} from './aiServiceSteps/aiStagingOrder/stagingOptionsConfig';

const useStyles = makeStyles({
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  roomType: {
    color: COLOR.BLACK,
    fontSize: 10,
    padding: 4,
    borderRadius: 8,
    border: `solid 1px ${COLOR.GREY}`,
    marginRight: 5,
  },
  roomStyle: {
    color: COLOR.BLACK,
    fontSize: 10,
    padding: 4,
    borderRadius: 8,
    border: `solid 1px ${COLOR.GREY}`,
  },
  generateButton: {
    color: 'white',
    marginRight: 10,
    '& p': {
      fontSize: 12,
    },
    backgroundColor: COLOR.LIGHT_BLUE,
    '@media (max-width: 768px)': {
      marginRight: 0,
      width: '100%',
    },
  },
  downloadButton: {
    '@media (max-width: 768px)': {
      width: '100%',
      marginBottom: 10,
      marginTop: 10,
    },
  },
  yesGenerateButton: {
    color: 'white',
    '& p': {
      fontSize: 16,
    },
    backgroundColor: COLOR.LIGHT_BLUE,
    '@media (max-width: 768px)': {
      '& p': {
        fontSize: 11,
      },
    },
  },
  noCancelButton: {
    color: COLOR.LIGHT_BLUE,
    border: `solid ${COLOR.LIGHT_BLUE} 1px`,
    '& p': {
      fontSize: 16,
    },
    backgroundColor: COLOR.WHITE,
    '@media (max-width: 768px)': {
      '& p': {
        fontSize: 11,
      },
    },
  },
  sliderContent: {
    width: '100%',
    display: 'flex',
    marginBottom: 10,
  },
  overlayContent: {
    position: 'relative',
    width: 'fit-content',
  },
  overlayText: {
    fontSize: 10,
    padding: 2,
    borderRadius: 4,
    backgroundColor: COLOR.TRANSPARENT_BLACK,
    color: COLOR.WHITE,
    position: 'absolute',
    bottom: 15,
    right: 5,
    '@media (max-width: 768px)': {
      right: 8,
      bottom: 10,
    },
  },
  overlayIcon: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    backgroundColor: COLOR.ULTRA_LIGHT_GREY,
    padding: 6,
    borderRadius: 4,
    cursor: 'pointer',
  },
  desktopViewSwitchBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    marginTop: 10,
    marginBottom: 10,
    '& p': {
      fontWeight: 700,
      fontSize: '1em',
    },
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  mobileViewSwitchBox: {
    display: 'none',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    marginTop: 10,
    marginBottom: 10,
    '& p': {
      fontWeight: 700,
      fontSize: '1em',
    },
    '@media (max-width: 768px)': {
      display: 'flex',
    },
  },
  image: {
    width: '100%',
    objectFit: 'cover',
    textAlign: 'center',
    '@media (max-width: 768px)': {
      width: '100%',
    },
  },
  roomOptions: {
    display: 'flex',
    flexDirection: 'row',
    '@media (max-width: 768px)': {
      marginBottom: 10,
    },
  },
  arrowContent: {
    width: 50,
    height: 50,
    color: COLOR.DARK_BLUE,
  },
  contentRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  slideContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 'auto',
    maxHeight: 500,
    overflowY: 'auto',
    border: `solid 1px ${COLOR.GREY}`,
    backgroundColor: `${COLOR.ULTRA_LIGHT_GREY}`,
    padding: 5,
    '@media (max-width: 768px)': {
      width: '100%',
      overflowY: 'hidden',
      overflowX: 'auto',
      maxHeight: 100,
      flexDirection: 'row',
    },
  },
  selectedSlideImage: {
    height: 'auto',
    width: '100%',
    objectFit: 'cover',
    outline: `solid 2px ${COLOR.LIGHT_BLUE}`,
    cursor: 'pointer',
    marginBottom: 5,
    '@media (max-width: 600px)': {
      height: 'auto',
      maxWidth: 100,
      marginRight: 5,
      marginBottom: 0,
    },
  },
  slideImage: {
    height: 'auto',
    width: '100%',
    cursor: 'pointer',
    objectFit: 'cover',
    marginBottom: 5,
    '@media (max-width: 600px)': {
      height: 'auto',
      maxWidth: 100,
      marginRight: 5,
      marginBottom: 0,
    },
  },
  desktopViewSlide: {
    display: 'flex',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  mobileViewSlide: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
    },
  },
  hiddenView: {
    display: 'none',
  },
  styleItem: {
    padding: 3,
    textAlign: 'center',
    borderRadius: 5,
    color: COLOR.BLACK,
    transition: 'color 0.5s ease',
    '& img': {
      borderRadius: 5,
    },
    '&:hover': {
      cursor: 'pointer',
      color: COLOR.LIGHT_BLUE,
    },
  },
  selectedStyle: {
    color: COLOR.LIGHT_BLUE,
    backgroundColor: COLOR.ULTRA_LIGHT_GREY,
    border: `solid ${COLOR.LIGHT_BLUE} 2px`,
  },
  roomStyleOptions: {
    borderRadius: 5,
    border: `solid ${COLOR.LIGHT_BLUE} 1px`,
    padding: 5,
    display: 'flex',
    flexDirection: 'column',
    // width: '100%',
    '@media (max-width: 767px)': {
      width: '95%',
      height: '100%',
    },
  },
  optionTitle: {
    fontWeight: 'bold',
    fontSize: 16,
    color: COLOR.LIGHT_BLUE,
    '@media (max-width: 767px)': {
      fontSize: 12,
    },
  },
  iterationModal: {
    display: 'flex',
    justifyContent: 'space-evenly',
    '@media (max-width: 1200px)': {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  incrementDecrementButtonsIcon: {
    color: COLOR.LIGHT_BLUE,
    width: 40,
    height: 40,
  },
  incrementDecrementButtons: {
    width: 40,
    height: 40,
  },
  statusIcon: {
    width: 50,
  },
  iconContainer: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
  pictureGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    width: 800,
    '@media (max-width: 1200px)': {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      width: 600,
    },
    '@media (max-width: 767px)': {
      gridTemplateColumns: '1fr 1fr',
    },
  },
  originalImageContainger: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    width: 'fit-content',
    '& img': {
      width: '70%',
    },
    '@media (max-width: 1200px)': {
      '& img': {
        width: '50%',
      },
    },
  },
});

interface Props {
  order_id: number;
  listing_id: number;
}

function GeneratedPhotosModal({ order_id, listing_id }: Props) {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const language = useLanguage();
  const { token } = useAuth();
  const { displayAlert } = useAlert();

  const [results, setResults] = useState<any[]>([]);
  const [carouselOpen, setCarouselOpen] = useState<boolean>(false);
  const [stagingOrder, setStagingOrder] = useState<any>(null);
  const [photoIndex, setPhotoIndex] = useState<number>(0);
  const [selectedResult, setSelectedResult] = useState<string>('');
  const [selectedResultId, setSelectedResultId] = useState<string>('');
  const [compareMode, setCompareMode] = useState<boolean>(false);
  const [generateIterationOptionModal, setgenerateIterationOptionModal] = useState<boolean>(false);
  const [selectedRoomStyle, setSelectedRoomStyle] = useState<string>('');
  const [amountOfIteration, setAmountOfIteration] = useState<number>(0);
  const [iterationsOnGoing, setIterationsOnGoing] = useState([]);
  const [MAX_ITERATION, setMAXITERATION] = useState<number>(0);
  const { onGoingIterations, orders } = useAiStagingOrder(listing_id, token);

  const fmtGeneratedPhotosTitle = intl.formatMessage({
    id: 'deliver.aiStagingOrder.title',
  });
  const fmtOriginal = intl.formatMessage({
    id: 'deliver.aiServices.original',
  });
  const fmtReGenerate = intl.formatMessage({
    id: 'deliver.aiServices.regenerate',
  });
  const fmtNoCancel = intl.formatMessage({ id: 'no.cancel' });
  const fmtDownload = intl.formatMessage({ id: 'download' });
  const fmtSomethingWentWrong = intl.formatMessage({ id: 'deliver.aiServices.downloadError' });
  const fmtOrderFailed = intl.formatMessage({ id: 'deliver.aiServices.stagingOrderError' });
  const fmtOrderSuccess = intl.formatMessage({ id: 'deliver.aiServices.stagingOrderSuccess' });
  const fmtCompare = intl.formatMessage({ id: 'compare' });
  const fmtRoomStyle = intl.formatMessage({ id: 'deliver.aiServices.roomStyle' });
  const fmtRoomType = intl.formatMessage({ id: 'deliver.aiServices.roomType' });
  const fmtPresentlySelected = intl.formatMessage({ id: 'deliver.aiServices.presentlySelected' });
  const fmtAmountOfIteration = intl.formatMessage({ id: 'deliver.aiServices.amountOfIteration' });

  useEffect(() => {
    const order = orders.find((_order: any) => {
      return _order.id === order_id;
    });
    setStagingOrder(order);
    setResults([...order.iterations].reverse());
    setSelectedResult(order.latest_iteration);
    setSelectedRoomStyle(order.options.style);
    setMAXITERATION(VARIATION_LIMIT - order.iteration_count);
  }, [order_id, orders]);

  useEffect(() => {
    if (onGoingIterations[order_id]) {
      setIterationsOnGoing(onGoingIterations[order_id]);
    }
  }, [onGoingIterations]);

  useEffect(() => {
    if (selectedResult && selectedResult !== '') {
      setSelectedResultId(selectedResult.match(/_([^_]+)\.jpe?g$/)[1]);
    }
  }, [selectedResult]);

  const downloadResult = async (image_url: string) => {
    try {
      await downloadSingleFile(image_url);
    } catch (error) {
      console.error(error);
      displayAlert(ALERT.ERROR, fmtSomethingWentWrong);
    }
  };

  const checkAndSetSize = (beforePicture: any, afterPicture: any) => {
    const modalWidth = $('#generatedPhotosModal').eq(0).width();
    const bodyWidth = window.innerWidth;
    const normalizedWidth = Math.min(beforePicture.width, afterPicture.width);
    const normalizedHeight = Math.min(beforePicture.height, afterPicture.height);
    const aspectRatio = normalizedHeight / normalizedWidth;
    const width = bodyWidth > 750 ? modalWidth * 0.9 : modalWidth;
    const height = width * aspectRatio;
    return { width, height };
  };

  function increment() {
    let amountToIncrement = 0;
    switch (amountOfIteration) {
      case 0:
        amountToIncrement = 1;
        break;
      case 1:
        amountToIncrement = 3;
        break;
      case 3:
        amountToIncrement = 5;
        break;
      case 5:
        amountToIncrement = 10;
        break;
      case 10:
        amountToIncrement = MAX_ITERATION;
        break;
      default:
        amountToIncrement = 1;
        break;
    }

    if (amountToIncrement > MAX_ITERATION) {
      amountToIncrement = MAX_ITERATION;
    }
    setAmountOfIteration(amountToIncrement);
  }

  function decrement() {
    switch (amountOfIteration) {
      case 1:
        setAmountOfIteration(0);
        break;
      case 3:
        setAmountOfIteration(1);
        break;
      case 5:
        setAmountOfIteration(3);
        break;
      case 10:
        setAmountOfIteration(5);
        break;
      case MAX_ITERATION:
        if (MAX_ITERATION < 3) {
          setAmountOfIteration(1);
        } else if (MAX_ITERATION < 5) {
          setAmountOfIteration(3);
        } else if (MAX_ITERATION < 10) {
          setAmountOfIteration(5);
        } else {
          setAmountOfIteration(10);
        }
        break;
      default:
        setAmountOfIteration(0);
        break;
    }
  }

  const regenerate = async () => {
    const config = {
      type: 'staging',
      room_type: stagingOrder.options.room_type,
      style: selectedRoomStyle,
      variation_count: amountOfIteration,
    };
    const res = await createAiStagingIteration(
      token,
      config,
      stagingOrder.listing_id,
      stagingOrder.render_id,
      stagingOrder.iteration_count,
    );
    if (!res || res.status !== 200) {
      displayAlert(ALERT.ERROR, fmtOrderFailed);
      return;
    }
    displayAlert(ALERT.SUCCESS, fmtOrderSuccess);
    setgenerateIterationOptionModal(false);
    dispatch(fetchAiStagingOrder(token, listing_id));
  };
  const confirmationModalButtons = (
    <>
      <HvButton
        className={classes.noCancelButton}
        style={{ marginRight: 10 }}
        onClick={() => {
          setgenerateIterationOptionModal(false);
        }}
      >
        <Typography>{fmtNoCancel}</Typography>
      </HvButton>
      <HvButton
        className={classes.yesGenerateButton}
        disabled={amountOfIteration === 0}
        onClick={() => {
          regenerate();
        }}
      >
        <Typography>{fmtReGenerate}</Typography>
      </HvButton>
    </>
  );

  const switchBox = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <Typography>{fmtCompare}</Typography>
        <Switch
          checked={compareMode}
          onChange={() => {
            setCompareMode(!compareMode);
          }}
          name='compare'
          color='primary'
          inputProps={{ 'aria-label': 'compare' }}
        />
      </div>
    );
  };

  function getThumbnail(url: string) {
    const lastIndex = url.lastIndexOf('.');
    const thumbnail_url = `${url.substring(0, lastIndex)}_thumb.jpg`;
    return thumbnail_url;
  }

  const photoPreview = () => {
    if (!selectedResult) return null;
    return (
      <div>
        {stagingOrder.render_settings && stagingOrder.render_settings[selectedResultId] && (
          <div className={classes.roomOptions}>
            <Typography className={classes.roomType}>
              {`${fmtRoomType}: ${getRoomType(
                stagingOrder.render_settings[selectedResultId].room_type,
                language,
              )}`}
            </Typography>

            <Typography className={classes.roomStyle}>
              {`${fmtRoomStyle}: ${getTitle(
                roomStyles[stagingOrder.render_settings[selectedResultId].style],
                language,
              )}`}
            </Typography>
          </div>
        )}

        <div className={classes.row}>
          <div className={classes.desktopViewSwitchBox}>{switchBox()}</div>
          <HvIconButton
            icon='retry'
            text={fmtReGenerate}
            className={classes.generateButton}
            onClick={() => {
              setgenerateIterationOptionModal(true);
            }}
            disabled={VARIATION_LIMIT === stagingOrder.iteration_count}
          />
          <HvIconButton
            icon='download'
            className={classes.downloadButton}
            text={fmtDownload}
            onClick={() => {
              downloadResult(selectedResult);
            }}
          />
          <div className={classes.mobileViewSwitchBox}>{switchBox()}</div>
        </div>
        {compareMode && stagingOrder.original_image && selectedResult ? (
          <div className={classes.sliderContent}>
            <HvBeforeAfterSlider
              beforeImage={stagingOrder.original_image}
              afterImage={selectedResult}
              checkAndSetSize={checkAndSetSize}
            />
          </div>
        ) : (
          <div style={{ position: 'relative', width: '100%', textAlign: 'center' }}>
            <img className={classes.image} src={selectedResult} alt='design generation result' />
            <img
              src={images.magnifier}
              className={
                selectedResult === stagingOrder.original_image
                  ? classes.hiddenView
                  : classes.overlayIcon
              }
              width={15}
              alt='zoom in'
              onClick={() => {
                setPhotoIndex(results.indexOf(selectedResult));
                setCarouselOpen(true);
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const photoStrip = () => {
    return (
      <div>
        <div className={classes.overlayContent}>
          <img
            src={stagingOrder.original_image.replace('.jpg', '_thumb.jpg')}
            className={
              selectedResult === stagingOrder.original_image
                ? classes.selectedSlideImage
                : classes.slideImage
            }
            alt='original'
            onClick={() => {
              setSelectedResult(stagingOrder.original_image);
            }}
          />
          <Typography className={classes.overlayText}>{fmtOriginal}</Typography>
        </div>
        <div className={classes.slideContainer}>
          {results.length > 0 &&
            results.map((result: any, index: number) => {
              return (
                <img
                  key={index}
                  className={
                    selectedResult === result ? classes.selectedSlideImage : classes.slideImage
                  }
                  src={getThumbnail(result)}
                  alt={`virtual_staging_render_${index}`}
                  onClick={() => {
                    setSelectedResult(result);
                  }}
                />
              );
            })}
          {iterationsOnGoing.length > 0 &&
            iterationsOnGoing.flatMap((job: any) => {
              const variationCount = job.data.render_config.variation_count || 1;
              return Array.from({ length: variationCount }, (_, index) => {
                return (
                  <div style={{ position: 'relative', marginBottom: 5 }} key={`${job.id}-${index}`}>
                    <div className={classes.iconContainer}>
                      <CircularProgress className={classes.statusIcon} />
                    </div>
                    <img
                      className={classes.slideImage}
                      style={{ margin: 0 }}
                      src={getThumbnail(stagingOrder.latest_iteration)}
                      alt={`on_going_iteration_${job.id}_${index}`}
                    />
                  </div>
                );
              });
            })}
        </div>
      </div>
    );
  };

  const modalContent = () => {
    return (
      stagingOrder &&
      results.length > 0 && (
        <div>
          <Grid container spacing={1} className={classes.contentRow}>
            <Grid item xs={12} sm={2} md={2} className={classes.desktopViewSlide}>
              {photoStrip()}
            </Grid>
            <Grid item xs={12} sm={10} md={10}>
              {photoPreview()}
            </Grid>
            {/* <Grid item xs={12} sm={12} md={12} className={classes.mobileViewSlide}>
              {photoStrip()}
            </Grid> */}
          </Grid>
          {carouselOpen && results.length !== 0 && (
            <div>
              <Lightbox
                mainSrc={results[photoIndex]}
                nextSrc={results[(photoIndex + 1) % results.length]}
                prevSrc={results[(photoIndex + results.length - 1) % results.length]}
                onCloseRequest={() => {
                  setCarouselOpen(false);
                }}
                onMoveNextRequest={() => {
                  return setPhotoIndex((photoIndex + 1) % results.length);
                }}
                onMovePrevRequest={() => {
                  return setPhotoIndex((photoIndex + results.length - 1) % results.length);
                }}
              />
            </div>
          )}
          {generateIterationOptionModal && (
            <HvModal
              title={intl.formatMessage({ id: 'deliver.aiServices.iterationOptions' })}
              maxWidth='lg'
              onClose={() => {
                setgenerateIterationOptionModal(false);
              }}
              buttons={confirmationModalButtons}
            >
              <div className={classes.iterationModal}>
                <div className={classes.originalImageContainger}>
                  <div>
                    <Typography style={{ fontWeight: 700, fontSize: 15 }}>{fmtOriginal}</Typography>
                    <img
                      src={stagingOrder.original_image}
                      style={{ marginBottom: 5 }}
                      alt='original_for_render'
                    />
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
                    <Typography className={classes.optionTitle} style={{ marginRight: 5 }}>
                      {`${fmtRoomType}:`}
                    </Typography>
                    <Typography>{getRoomType(stagingOrder.options.room_type, language)}</Typography>
                  </div>
                  <Typography className={classes.optionTitle}>{fmtRoomStyle}</Typography>
                  <Typography style={{ fontSize: 14, color: COLOR.DARK_GREY, marginBottom: 5 }}>
                    {`${fmtPresentlySelected}: ${getTitle(
                      roomStyles[selectedRoomStyle],
                      language,
                    )}`}
                  </Typography>
                  <div className={classes.pictureGrid}>
                    {Object.keys(roomStyles).map((key) => {
                      const roomStyle = roomStyles[key];
                      return (
                        <div
                          key={key}
                          className={classNames(
                            classes.styleItem,
                            selectedRoomStyle === key && classes.selectedStyle,
                          )}
                          onClick={() => {
                            setSelectedRoomStyle(key);
                          }}
                        >
                          <img
                            width={210}
                            src={getRoomStyleImage(stagingOrder.options.room_type, key)}
                            alt='room style'
                          />
                          <Typography>{getTitle(roomStyle, language)}</Typography>
                        </div>
                      );
                    })}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 10,
                    }}
                  >
                    <Typography className={classes.optionTitle} style={{ marginRight: 5 }}>
                      {`${fmtAmountOfIteration}:`}
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        disabled={amountOfIteration === 0}
                        className={classes.incrementDecrementButtons}
                        onClick={() => {
                          decrement();
                        }}
                        style={{ marginRight: 20 }}
                      >
                        <RemoveCircleOutlineRoundedIcon
                          className={classes.incrementDecrementButtonsIcon}
                        />
                      </IconButton>
                      <Typography style={{ marginLeft: 10, marginRight: 10 }}>
                        {amountOfIteration}
                      </Typography>
                      <IconButton
                        disabled={MAX_ITERATION === amountOfIteration}
                        className={classes.incrementDecrementButtons}
                        style={{ marginLeft: 20 }}
                        onClick={() => {
                          increment();
                        }}
                      >
                        <AddCircleOutlineRoundedIcon
                          className={classes.incrementDecrementButtonsIcon}
                        />
                      </IconButton>
                    </div>
                  </div>
                  <FormattedMessage
                    id='deliver.aiServices.amountOfIterationLeftAfter'
                    values={{
                      iteration_count: MAX_ITERATION - amountOfIteration,
                      typography: (str: string) => {
                        return <Typography style={{ marginBottom: 10 }}>{str}</Typography>;
                      },
                    }}
                  />
                </div>
              </div>
            </HvModal>
          )}
        </div>
      )
    );
  };

  return (
    <HvModal
      id='generatedPhotosModal'
      maxWidth='md'
      title={fmtGeneratedPhotosTitle}
      headerImage='virtualStagingBg'
      onClose={() => {
        dispatch(hideModal(MODAL.GENERATED_PHOTOS));
      }}
    >
      {modalContent()}
    </HvModal>
  );
}
export default GeneratedPhotosModal;
